/**
 * Turning an auth failure into something a scorer can act on.
 *
 * Deliberately thin. The API's own `message` is already written for a human — "that
 * email and password do not match" — so it is shown as sent rather than translated
 * here. A client-side lookup table keyed by `code` would be a second copy of prose the
 * server owns, and the copies drift.
 *
 * Two things the server genuinely cannot do from where it stands, which is all this
 * module is for:
 *
 * 1. **Google failures arrive as a slug in the query string**, not as prose, because
 *    they come back through a 302 to the SPA rather than a JSON body. Something has to
 *    turn `email_taken` into a sentence, and it has to be here.
 * 2. **What to offer next.** A 409 on sign-up means the account exists, so the useful
 *    response is to move the scorer to sign-in with his email still filled in — a
 *    decision about this screen, not about the request.
 */
import { ApiRequestError } from "../api.ts";

export type AuthMode = "sign_in" | "sign_up";

export interface AuthProblem {
  readonly message: string;
  /**
   * The API's `code`, or `"unreachable"` when the request never landed.
   *
   * Kept alongside the prose because the friction record wants a *key* it can group by
   * (`friction.ts`) — a message is written for one human on one screen and cannot be
   * counted across a field test.
   */
  readonly code: string;
  /** Field-level hints from the API, keyed by field name. Empty when there are none. */
  readonly fields: Readonly<Record<string, string>>;
  /** The mode to offer switching to, when the failure says the scorer picked wrong. */
  readonly suggest: AuthMode | null;
}

const NO_FIELDS: Readonly<Record<string, string>> = {};

/**
 * Read a thrown value into something renderable.
 *
 * Handles a non-`ApiRequestError` too, because that is the offline case: `fetch` rejects
 * with a `TypeError` and no status at all. On a cricket ground that is the *likely*
 * failure, not the exotic one, so it gets a message that says what to do rather than
 * "Failed to fetch".
 */
export function authProblem(error: unknown, mode: AuthMode): AuthProblem {
  if (!(error instanceof ApiRequestError)) {
    return {
      message: "Could not reach the server. Check your connection and try again.",
      code: "unreachable",
      fields: NO_FIELDS,
      suggest: null,
    };
  }

  const { code, message, fields } = error.body;

  // An account already exists. On sign-up that is the commonest way in — the scorer is
  // returning and picked the wrong tab — so the screen offers the other one rather than
  // leaving him to work it out.
  const suggest: AuthMode | null =
    code === "conflict" ? (mode === "sign_up" ? "sign_in" : null) : null;

  return { message, code, fields: fields ?? NO_FIELDS, suggest };
}

/**
 * What the `reason` slug on `/?auth=failed&reason=…` means.
 *
 * The server sanitises this to `/^[a-z_]{1,40}$/` and replaces anything else with
 * `failed`, so it is safe to render — but it is still an untrusted string from the
 * address bar, which is why it is only ever used as a lookup key and never shown raw.
 * An unrecognised slug falls back to the generic sentence rather than to itself.
 */
const GOOGLE_FAILURES: Readonly<Record<string, string>> = {
  // The PKCE verifier cookie is gone: ten minutes elapsed, the tab was reopened from
  // history, or this callback was never started from our own sign-in button.
  expired: "That sign-in link has expired. Tap Continue with Google again.",
  email_taken:
    "That Google account's email already belongs to an account here. Sign in with the email and password you used first.",
  access_denied: "Google sign-in was cancelled.",
  invalid_credentials: "Google could not confirm that account.",
  email_unconfirmed: "Confirm your email address, then sign in again.",
  rate_limited: "Too many attempts just now. Wait a minute and try again.",
  unavailable: "Google sign-in is temporarily unavailable. Try email instead.",
  rejected: "Google sign-in was refused.",
  failed: "Google sign-in did not complete. Try again, or use email.",
};

const GENERIC_GOOGLE_FAILURE = GOOGLE_FAILURES["failed"] as string;

/**
 * Read the outcome of a Google redirect off the current url.
 *
 * Success carries **no** query string at all — the server 302s to `/` and the only
 * evidence is that `GET /api/me` now answers 200 — so there is nothing to detect here
 * for the happy path, and this returns null for it.
 */
export function googleFailureFrom(search: string): { reason: string; message: string } | null {
  const params = new URLSearchParams(search);
  if (params.get("auth") !== "failed") return null;
  const reason = params.get("reason") ?? "failed";
  return { reason, message: GOOGLE_FAILURES[reason] ?? GENERIC_GOOGLE_FAILURE };
}
